import { useSocket } from '@folklore/socket';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { isNewer, mergeGame } from '../lib/utils';

import { useContest } from './ContestContext';
import { LabelsProvider } from './LabelsContext';

interface GameContextType {
    game: Game | null;
    participation: Participation | null;
}

const GameContext = React.createContext<GameContextType>({
    game: null,
    participation: null,
});

export function useGameContext(): GameContextType {
    return useContext(GameContext);
}

export function useGame(): Game {
    const { game } = useGameContext();
    return game;
}

export function useParticipation(): Participation | null {
    const { participation = null } = useGameContext();
    return participation;
}

interface GameProviderProps {
    children: React.ReactNode;
    game: Game;
    participation?: Participation;
    followCurrent?: boolean;
}

export const GameProvider = ({
    children,
    game: providedGame,
    participation: providedParticipation = null,
    followCurrent = false,
}: GameProviderProps) => {
    const { locale } = useIntl();
    const { id: contestId = null } = useContest();
    const queryClient = useQueryClient();
    const [socketGame, setSocketGame] = useState(providedGame);
    const [socketParticipation, setSocketParticipation] = useState(providedParticipation);

    const game = useMemo(
        () => (isNewer(providedGame, socketGame) ? providedGame : socketGame),
        [providedGame, socketGame],
    );

    const participation = useMemo(
        () =>
            isNewer(providedParticipation, socketParticipation)
                ? providedParticipation
                : socketParticipation,
        [providedParticipation, socketParticipation],
    );

    const onSocketMessage = useCallback(
        ({ event, data: newData = null }) => {
            if (event === 'game.updated') {
                const { id: gameId = null } = game || {};
                const { id: newGameId = null } = newData || {};
                if (newData !== null && gameId === newGameId) {
                    const newGame = mergeGame(game, newData);
                    setSocketGame(newGame);
                    queryClient.setQueryData(['game', gameId], newGame);
                }
            }

            if (event === 'game.question_resetted' && participation !== null) {
                const { questionId } = newData || {};
                const newParticipation = {
                    ...participation,
                    answers: (participation?.answers || []).filter(
                        (answer) => answer.question !== questionId,
                    ),
                };
                setSocketParticipation(newParticipation);
                queryClient.setQueryData(['participation', participation.id], newParticipation);
            }

            if (event === 'participation.updated') {
                const { id: participationId = null } = participation || {};
                const { id: newParticipationId = null } = newData || {};
                if (participationId === newParticipationId && isNewer(newData, participation)) {
                    const newParticipation = {
                        ...participation,
                        ...newData,
                    };
                    setSocketParticipation(newParticipation);
                    queryClient.setQueryData(
                        ['participation', newParticipationId],
                        newParticipation,
                    );
                }
            }
        },
        [queryClient, game, participation],
    );

    const { id: gameId = null } = game || {};
    const { id: participationId = null } = participation || {};
    const channels = useMemo(
        () =>
            [
                gameId !== null ? `game.${gameId}.${locale}` : null,
                gameId !== null ? `game.${gameId}` : null,
                participationId !== null ? `participation.${participationId}` : null,
            ].filter((it) => it !== null),
        [locale, contestId, gameId, participationId, followCurrent],
    );

    useSocket(channels, {
        onMessage: onSocketMessage,
    });

    const value = useMemo(
        () => ({
            game,
            participation,
        }),
        [game, participation],
    );

    const { labels } = game || {};

    return (
        <GameContext.Provider value={value}>
            <LabelsProvider labels={labels} prefix="game">
                {children}
            </LabelsProvider>
        </GameContext.Provider>
    );
};

export default GameContext;
